import Quill from "quill";
import React, { useCallback, useEffect } from "react"; // React をインポート
import { useFileContext } from "../../../../context/FileContext";
import { useAlert } from "../../../../context/AlertContext";

interface ImageHandlerProps {
  quill: Quill | null;
  fileUuid: string | undefined;
}

const ImageHandlerComponent = ({ quill, fileUuid }: ImageHandlerProps) => {
  // コンポーネント名を変更
  const {
    unsplash: unsplashMap,
    generatedImage: generatedImageMap,
    sidebarGenerateImage: sidebarGenerateImageMap,
    setFileText: contextSetFileText,
    setGeneratedImage: contextSetGeneratedImage,
    setSidebarGenerateImage: contextSetSidebarGenerateImage,
    setUnsplash: contextSetUnsplash,
  } = useFileContext();
  const { setAlert: setGlobalAlert } = useAlert();

  const unsplash = fileUuid ? unsplashMap[fileUuid] : undefined;
  const generatedImage = fileUuid ? generatedImageMap[fileUuid] : undefined;
  const sidebarGenerateImage = fileUuid ? sidebarGenerateImageMap[fileUuid] : undefined;

  // UnsplashのURLから画像挿入
  useEffect(() => {
    if (!quill || !unsplash || !fileUuid) return;

    const insertUnsplashImage = () => {
      const range = quill.getSelection(true);
      const index = range ? range.index : 0;
      quill.history.cutoff();
      quill.updateContents([{ retain: index }, { insert: { image: { src: unsplash, class: "unsplash" } } }], "user");
      setGlobalAlert("success", "Image uploaded");
      quill.setSelection(index + 1, 0);
      quill.history.cutoff();
      contextSetUnsplash({ uuid: fileUuid, url: "" }); // Reset unsplash URL after insertion
    };

    insertUnsplashImage();
  }, [unsplash, quill, fileUuid, contextSetUnsplash, setGlobalAlert]);

  // 生成された画像の挿入
  useEffect(() => {
    if (!quill || !generatedImage || !fileUuid) return;

    const insertGeneratedImage = () => {
      const range = quill.getSelection(true);
      const index = range ? range.index : 0;

      quill.history.cutoff(); // 履歴の区切りを入れる

      quill.updateContents(
        [
          { retain: index },
          {
            insert: {
              image: {
                src: generatedImage,
                "data-type": "temp-image",
                "data-generated": "true",
                class: "generated",
              },
            },
          },
        ],
        "user"
      );

      quill.setSelection(index + 1, 0);
      setGlobalAlert("success", "Generated image inserted");
      contextSetFileText({ uuid: fileUuid, content: quill.getText() });
      contextSetGeneratedImage({ uuid: fileUuid, url: "" });

      quill.history.cutoff(); // 履歴の区切りを入れる
    };

    insertGeneratedImage();
  }, [generatedImage, quill, fileUuid, contextSetFileText, contextSetGeneratedImage, setGlobalAlert]);

  // サイドバーから生成された画像の挿入
  useEffect(() => {
    if (!quill || !sidebarGenerateImage || !fileUuid) return;

    const insertSidebarGeneratedImage = () => {
      const range = quill.getSelection(true);
      const index = range ? range.index : 0;

      quill.history.cutoff(); // 履歴の区切りを入れる

      quill.updateContents(
        [
          { retain: index },
          {
            insert: {
              image: {
                src: sidebarGenerateImage,
                "data-type": "temp-image",
                "data-generated": "true",
                class: "generated",
              },
            },
          },
        ],
        "user"
      );

      quill.setSelection(index + 1, 0);
      setGlobalAlert("success", "Generated image inserted");
      contextSetFileText({ uuid: fileUuid, content: quill.getText() });
      contextSetSidebarGenerateImage(""); // Reset sidebar image URL

      quill.history.cutoff(); // 履歴の区切りを入れる
    };

    insertSidebarGeneratedImage();
  }, [sidebarGenerateImage, quill, fileUuid, contextSetFileText, contextSetSidebarGenerateImage, setGlobalAlert]);

  // 画像貼り付けの処理
  const pasteImage = useCallback(
    (e: ClipboardEvent) => {
      if (!quill) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      const itemsArray = Array.from(items);
      for (const element of itemsArray) {
        if (element.type.indexOf("image") === -1) continue;

        const file = element.getAsFile();
        if (!file) continue;

        if (file.size > 20 * 1024 * 1024) {
          setGlobalAlert("error", "File size exceeds 20MB. Please paste a smaller file.");
          return;
        }

        const reader = new FileReader();
        reader.onload = (eventReader) => {
          const base64Image = eventReader.target?.result as string;
          const range = quill.getSelection(true);
          const index = range ? range.index : 0;

          quill.history.cutoff(); // 履歴の区切りを入れる

          quill.updateContents(
            [
              { retain: index },
              {
                insert: {
                  image: {
                    src: base64Image,
                    "data-type": "temp-image",
                    class: "uploaded",
                  },
                },
              },
            ],
            "user"
          );

          quill.setSelection(index + 1, 0);
          setGlobalAlert("success", "Image pasted successfully");

          quill.history.cutoff(); // 履歴の区切りを入れる
        };
        reader.readAsDataURL(file);
      }
    },
    [quill, setGlobalAlert]
  );

  // 画像ドロップの処理
  useEffect(() => {
    if (!quill) return;

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;

      const file = files[0];
      if (file.size > 20 * 1024 * 1024) {
        setGlobalAlert("error", "File size exceeds 20MB. Please drop a smaller file.");
        return;
      }

      const reader = new FileReader();
      reader.onload = (eventReader) => {
        const base64Image = eventReader.target?.result as string;
        const range = quill.getSelection(true);
        const index = range ? range.index : 0;

        quill.history.cutoff(); // 履歴の区切りを入れる

        quill.updateContents(
          [
            { retain: index },
            {
              insert: {
                image: {
                  src: base64Image,
                  "data-type": "temp-image",
                  class: "uploaded",
                },
              },
            },
          ],
          "user"
        );

        quill.setSelection(index + 1, 0);
        setGlobalAlert("success", "Image dropped successfully");

        quill.history.cutoff(); // 履歴の区切りを入れる
      };
      reader.readAsDataURL(file);
    };

    quill.root.addEventListener("drop", handleDrop);
    return () => {
      quill.root.removeEventListener("drop", handleDrop);
    };
  }, [quill, setGlobalAlert]);

  // 画像貼り付けのイベントリスナー
  useEffect(() => {
    if (!quill) return;

    const handlePaste = (e: ClipboardEvent) => {
      e.preventDefault();
      pasteImage(e);
    };

    quill.root.addEventListener("paste", handlePaste);
    return () => {
      quill.root.removeEventListener("paste", handlePaste);
    };
  }, [quill, pasteImage]);

  return null; // このコンポーネントはUIを持たない
};
export default React.memo(ImageHandlerComponent); // React.memoでラップ
